import request from '@/utils/request'
import { useAccountStore } from '@/stores/account'
import type { AxiosResponse } from 'axios'
import type { LaravelCollection, LaravelResource, LaravelPaginated } from './types'
import type { Account } from '@/models/Account'
import type { Member } from '@/models/Member'

interface ListQueryArg {
  page?: number | null
  limit?: number | null
}

interface MemberFilter {
  roles?: string | string[]
  username?: string
  email?: string
  [key: string]: string | string[] | undefined
}

/**
 * GET /api/v1/users/me/accounts — every active account of the signed-in person, each with its
 * account signature. The backend takes the person from the access token; the path id is ignored.
 */
export async function getAccounts(): Promise<AxiosResponse<LaravelCollection<Account>>> {
  return request({
    url: '/api/v1/users/me/accounts',
    method: 'get',
  })
}

/** GET /api/v1/account_name/{domain} — 200 if exists, 404 otherwise (public) */
export async function checkIfDomainExists(domain: string): Promise<AxiosResponse<void>> {
  return request({
    url: '/api/v1/account_name/' + domain,
    method: 'get',
  })
}

/** GET /api/v1/accounts/{id} — show current account details */
export async function getAccountInfos(): Promise<AxiosResponse<LaravelResource<Account>>> {
  const account = await useAccountStore().getCurrentAccount()
  return request({
    url: '/api/v1/accounts/' + account.data.account_id,
    method: 'get',
  })
}

/** GET /api/v1/accounts/members — paginated members of current account */
export function getMembers(
  list_query?: ListQueryArg | null,
  filter?: MemberFilter | null,
  search?: string | null,
  unscoped: boolean = true,
): Promise<AxiosResponse<LaravelPaginated<Member>>> {
  const query_params: string[] = []
  if (list_query?.page) query_params.push('page=' + list_query.page)
  if (list_query?.limit) query_params.push('limit=' + list_query.limit)
  if (filter) {
    for (const [key, value] of Object.entries(filter)) {
      if (value !== undefined) query_params.push('filter[' + key + ']=' + value)
    }
  }
  query_params.push('unscoped=' + unscoped)
  if (search) query_params.push('search=' + search)
  return request({
    url: '/api/v1/accounts/members?' + query_params.join('&'),
    method: 'get',
  })
}

/** PATCH /api/v1/accounts/members/{user_id}/attributes/status */
export async function changeMemberStatus(
  user_id: string,
  status: string,
): Promise<AxiosResponse<LaravelResource<Account>>> {
  return request({
    url: '/api/v1/accounts/members/' + user_id + '/attributes/status',
    data: { status },
    method: 'patch',
  })
}
